import { reactive } from "vue";
import isTaxInfoConfigured from "@/mixins/isTaxInfoConfigured";

// Single source of truth for "does this participant still owe us tax info?".
//
// Three components gate on the answer - the card tile in
// MarqetaWidgetManagerV2, the marketplace tile in RedeemRewards, and the
// cardholder documents in MarqetaFooter. Each used to own a copy of the fetch,
// the flag and the pairing with `canCollectTaxInfo`, and the copies had already
// drifted: one skipped the fetch entirely for tenants without a payment card,
// which left its gate permanently open on points-only clients. Keeping the
// decision here means every consumer opens and closes in lockstep.
//
// Pulls in `isTaxInfoConfigured` so `canCollectTaxInfo` is always available -
// the per-user flag below is meaningless without the per-tenant guard.

// All three consumers mount together on one wallet render, which would fire
// three identical GET /v3/user/tax-info/status calls. This collapses them into
// one by sharing the in-flight promise.
//
// Deliberately NOT a cache: the promise is dropped the moment it settles, so a
// later call (RedeemRewards re-checking on click, or a remount after tax info
// was captured on the profile page) always reaches the server. Only genuinely
// concurrent callers share a result, which is exactly the duplication here.
let inFlightStatusRequest = null;

// Tax info is one fact about the user, but every consumer keeps its own copy of
// the answer and guards its own section of the wallet. Capturing it in the card
// drawer has to settle the redeem tile as well, and the other way round -
// otherwise the section the user did not submit from sits behind a prompt asking
// for something they have already provided, until a reload.
//
// Two separate signals, because the two halves want different timing:
//
//   gate    - bumped as soon as the save succeeds. Purely local state, costs no
//             network, and dropping the prompt immediately is what makes the UI
//             feel right.
//   refresh - bumped when the drawer closes. Re-reading the rewards balance makes
//             the rewards service push this participant's transactions to
//             Pointbank, and saving tax info has just kicked off a fire-and-forget
//             release that pushes them too. Firing at save time runs the two
//             concurrently, which is how a single award ends up minted twice.
//             Waiting for the user to dismiss the confirmation keeps them apart.
//
// A shared counter rather than an event bus: Vue 3 removes $on/$off, and this
// reaches consumers that are not in each other's component tree (the wallet
// footer is neither parent nor child of the tiles).
const taxInfoSignal = reactive({ gate: 0, refresh: 0 });

// Test-only. Consumers read the tokens on mount, so values left over from a
// previous test would look like a save that had already happened.
export function resetTaxInfoSavedSignal() {
  taxInfoSignal.gate = 0;
  taxInfoSignal.refresh = 0;
}

function fetchTaxInfoStatus() {
  if (!inFlightStatusRequest) {
    // Imported here rather than at the top of the file, and it has to stay that
    // way. UserServiceV3 -> services/index -> store -> router -> WalletV3 ->
    // MarqetaWidgetManagerV2 -> this mixin is a cycle, and this mixin is read at
    // module-evaluation time by `mixins: [...]`. A static import puts it on the
    // cycle, so whichever module the bundle happens to start with can reach
    // `mixins: [taxInfoGateMixin]` while this file is still initialising - the
    // mixin is then undefined, and Vue dies merging it with "Cannot read
    // properties of undefined (reading 'props')" when the wallet renders.
    // Deferring to first call means the cycle is closed before anyone travels it.
    inFlightStatusRequest = import("@/services/UserServiceV3")
      .then((module) => module.default.getTaxInfoStatus())
      .finally(() => {
        inFlightStatusRequest = null;
      });
  }

  return inFlightStatusRequest;
}

// Test-only. A request left in flight when a test ends would otherwise be
// handed to the next test, which has already swapped the mock.
export function resetTaxInfoStatusRequest() {
  inFlightStatusRequest = null;
}

export default {
  mixins: [isTaxInfoConfigured],
  data() {
    return {
      // Server's answer for this user. Prefer `showTaxInfoGate` when branching;
      // this is only the raw half of the pair.
      taxInfoRequired: false,
      // Bumped by every check and by clearTaxInfoGate, so a response that comes
      // back after something newer has already decided the answer is discarded
      // instead of overwriting it. Without this, the status fetch started on
      // mount can land after the user has saved tax info and re-raise the gate
      // on a stale answer.
      taxInfoStatusGeneration: 0,
      // Set just before this instance broadcasts a refresh, so it does not react
      // to its own signal - it refreshes itself as part of handling the close.
      taxInfoRefreshedHere: false,
    };
  },
  computed: {
    // The one thing consumers should render against. Pairs the per-user server
    // decision with the per-tenant "are we even allowed to ask" guard, so a
    // tenant that never collects tax info can never be gated.
    showTaxInfoGate() {
      return this.canCollectTaxInfo && this.taxInfoRequired;
    },
    taxInfoGateToken() {
      return taxInfoSignal.gate;
    },
    taxInfoRefreshToken() {
      return taxInfoSignal.refresh;
    },
  },
  watch: {
    // Another section captured tax info for this user. The answer is known
    // first-hand, so drop the gate without a round trip. No suppression needed:
    // the instance that saved has already done exactly this, and repeating it is
    // a no-op.
    taxInfoGateToken() {
      this.taxInfoStatusGeneration++;
      this.taxInfoRequired = false;
    },
    // The drawer that captured tax info has closed, so whatever it unblocked is
    // safe to re-read now.
    taxInfoRefreshToken() {
      if (this.taxInfoRefreshedHere) {
        this.taxInfoRefreshedHere = false;
        return;
      }

      if (typeof this.onTaxInfoCapturedElsewhere === "function") {
        this.onTaxInfoCapturedElsewhere();
      }
    },
  },
  methods: {
    // Safe to call on mount from any consumer, and again later to re-check.
    // Deliberately NOT conditioned on payment cards being enabled: tax info
    // also gates points redemption, so a points-only tenant still has to know
    // whether it is outstanding.
    async checkTaxInfoStatus() {
      const generation = ++this.taxInfoStatusGeneration;

      if (!this.canCollectTaxInfo) {
        this.taxInfoRequired = false;
        return;
      }

      try {
        const status = await fetchTaxInfoStatus();
        if (generation !== this.taxInfoStatusGeneration) return;
        // `taxInfoRequired` is the server's decision and is taken as
        // authoritative rather than re-derived from `hasTaxInfo`.
        // microservice-user currently returns `taxInfoRequired: !hasTaxInfo`,
        // but it owns that rule and its JSDoc anticipates it growing conditions
        // (an earnings threshold, periodic re-certification). Under
        // re-certification `hasTaxInfo` and `taxInfoRequired` are both true, so
        // an `&& !hasTaxInfo` here would silently drop the gate.
        this.taxInfoRequired = !!status?.result?.taxInfoRequired;
      } catch (err) {
        if (generation !== this.taxInfoStatusGeneration) return;
        // Fail open. A status lookup that cannot complete should not lock
        // someone out of their own wallet; the redeem and card-issue calls each
        // enforce the requirement server-side anyway.
        console.log("Error checking tax info status: ", err);
        this.taxInfoRequired = false;
      }
    },
    // Lift the gate after a successful save, so the UI updates without a reload
    // and without waiting on another round trip. Supersedes any check still in
    // flight - we know the answer first-hand, so a slower response must not
    // undo it. Also settles the other consumers, which hold their own copies.
    //
    // Deliberately does not trigger any refetching; see broadcastTaxInfoRefresh.
    clearTaxInfoGate() {
      this.taxInfoStatusGeneration++;
      this.taxInfoRequired = false;
      taxInfoSignal.gate++;
    },
    // Call once the drawer that captured tax info has closed. Tells the other
    // sections to re-read whatever their gate was hiding, at a point where the
    // release triggered by the save has had time to finish rather than racing it.
    broadcastTaxInfoRefresh() {
      this.taxInfoRefreshedHere = true;
      taxInfoSignal.refresh++;
    },
  },
};
